import React, { useCallback } from "react";
import { Redirect, Route, Switch, useParams } from "react-router-dom";

import {
  LOAD_DEVICE_STATS,
  unloadDeviceStatsAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/device-stats/actions";
import { deviceStatsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/device-stats/selectors";
import InternalStateTable from "cortex/ui/src/fleet/view/device/component/InternalStateTable";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { getAuthUser } from "cortex/ui/src/redux-store/auth";
import { deviceStatusesSelector } from "cortex/ui/src/redux-store/device-statuses/selectors";
import {
  CLEAR_CACHE,
  CLEAR_WEBCAM_SNAPSHOT_CACHE,
  RESTART_DEVICE,
} from "cortex/ui/src/redux-store/devices/actions";
import { deviceStoresLoadedSelector } from "cortex/ui/src/redux-store/devices/selectors";
import { defaultObject } from "cortex/ui/src/redux-store/selectors";
import { currentDeviceSelector } from "cortex/ui/src/redux-store/ui-state/current-device/selectors";
import { FetchController as Fetch } from "cortex/ui/src/ui/fetch";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { User } from "cortex/ui/src/user/model";
import { Container } from "cortex/ui/src/view/layout";

import DeviceDetails from "./DeviceDetails";
import DeviceEvents from "./DeviceEvents";
import DeviceLogs from "./DeviceLogs";
import DeviceOverview from "./DeviceOverview";
import DeviceViewPage from "./DeviceViewPage";
import { Device, DeviceStatus, Stats } from "./model";

interface DeviceViewProps {
  activeTab?: string;
  clearPlayerCache: (deviceId: number | string) => Promise<void>;
  clearWebcamSnapshotCache: (deviceId: number | string) => Promise<void>;
  device?: Device;
  deviceId: string;
  deviceStatus?: DeviceStatus;
  isThirdParty: boolean;
  loadDeviceStats: (deviceId: string) => Promise<void>;
  restartDevice: (deviceId: number | string) => Promise<void>;
  stats?: Stats;
  storesLoaded: boolean;
  unloadDeviceStats: () => void;
  user?: User;
}

interface DeviceViewContentProps {
  activeTab?: string;
  deviceId: string;
}

export function DeviceView({
  activeTab = "",
  clearPlayerCache,
  clearWebcamSnapshotCache,
  device,
  deviceId,
  deviceStatus,
  isThirdParty,
  loadDeviceStats,
  restartDevice,
  stats,
  storesLoaded,
  unloadDeviceStats,
  user,
}: DeviceViewProps) {
  if (!storesLoaded) {
    return (
      <Container>
        <NavLoader.Proxy />
      </Container>
    );
  }

  if (!device) {
    return <Redirect to="/404" />;
  }

  return (
    <DeviceViewPage
      activeTab={activeTab}
      clearPlayerCache={clearPlayerCache}
      clearWebcamSnapshotCache={clearWebcamSnapshotCache}
      device={device}
      deviceId={deviceId}
      deviceStatus={deviceStatus}
      isThirdParty={isThirdParty}
      loadDeviceStats={loadDeviceStats}
      restartDevice={restartDevice}
      storesLoaded={storesLoaded}
      unloadDeviceStats={unloadDeviceStats}
      user={user}
    >
      <Switch>
        <Route
          path="/devices/:deviceId/overview"
          render={() => (
            <DeviceOverview
              device={device}
              deviceId={deviceId}
              isThirdParty={isThirdParty}
              stats={stats}
              storesLoaded={storesLoaded}
            />
          )}
        />
        {!isThirdParty && (
          <Route
            path="/devices/:deviceId/details"
            render={() => (
              <DeviceDetails
                device={device}
                deviceId={deviceId}
                deviceStatus={(deviceStatus ?? defaultObject) as DeviceStatus}
                storesLoaded={storesLoaded}
              />
            )}
          />
        )}
        <Route
          path="/devices/:deviceId/internals"
          render={() => <InternalStateTable device={device} />}
        />
        <Route
          path="/devices/:deviceId/logs"
          render={() => (
            <DeviceLogs device={device} storesLoaded={storesLoaded} />
          )}
        />
        <Route
          path="/devices/:deviceId/events"
          render={() => (
            <DeviceEvents device={device} storesLoaded={storesLoaded} />
          )}
        />
        <Redirect
          from="/devices/:deviceId"
          to={`/devices/${deviceId}/overview`}
        />
      </Switch>
    </DeviceViewPage>
  );
}

function DeviceViewContent({ activeTab, deviceId }: DeviceViewContentProps) {
  const dispatch = useAppDispatch();
  const storesLoaded = useAppSelector(deviceStoresLoadedSelector);
  const device = useAppSelector(currentDeviceSelector);
  const deviceStatuses = useAppSelector(deviceStatusesSelector);
  const stats = useAppSelector(deviceStatsSelector);
  const user = useAppSelector(getAuthUser);
  const devicePk = device?.id;
  const currentDeviceStatus =
    devicePk === undefined ? undefined : deviceStatuses[String(devicePk)];
  const clearPlayerCache = useCallback(
    (targetDeviceId: number | string) => {
      return createPromiseApiAction(dispatch, CLEAR_CACHE, [
        targetDeviceId,
      ]) as Promise<void>;
    },
    [dispatch],
  );
  const clearWebcamSnapshotCache = useCallback(
    (targetDeviceId: number | string) => {
      return createPromiseApiAction(dispatch, CLEAR_WEBCAM_SNAPSHOT_CACHE, [
        targetDeviceId,
      ]) as Promise<void>;
    },
    [dispatch],
  );
  const loadDeviceStats = useCallback(
    (targetDeviceId: string) => {
      return createPromiseApiAction(dispatch, LOAD_DEVICE_STATS, [
        targetDeviceId,
      ]) as Promise<void>;
    },
    [dispatch],
  );
  const restartDevice = useCallback(
    (targetDeviceId: number | string) => {
      return createPromiseApiAction(dispatch, RESTART_DEVICE, [
        targetDeviceId,
      ]) as Promise<void>;
    },
    [dispatch],
  );
  const unloadDeviceStats = useCallback(() => {
    dispatch(unloadDeviceStatsAction);
  }, [dispatch]);

  return (
    <DeviceView
      activeTab={activeTab}
      clearPlayerCache={clearPlayerCache}
      clearWebcamSnapshotCache={clearWebcamSnapshotCache}
      device={device ?? undefined}
      deviceId={deviceId}
      deviceStatus={currentDeviceStatus}
      isThirdParty={Boolean(device && device.is_thirdparty)}
      loadDeviceStats={loadDeviceStats}
      restartDevice={restartDevice}
      stats={stats}
      storesLoaded={storesLoaded}
      unloadDeviceStats={unloadDeviceStats}
      user={user ?? undefined}
    />
  );
}

export default function DeviceViewRoute() {
  const { id, activeTab } = useParams<{ id: string; activeTab?: string }>();

  return (
    <Fetch.Device deviceId={id}>
      <DeviceViewContent activeTab={activeTab} deviceId={id} />
    </Fetch.Device>
  );
}
